import { CommonModule, Location } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Input,
    NgModule,
    NO_ERRORS_SCHEMA,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { GoogleMap, GoogleMapsModule, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { Params, Router, RouterModule } from '@angular/router';
import { getBuildIdQuery, ISettingsResponse, MapSpot, NcgLocationCategory, NcgLocationMapCategory, NgcApiLocation } from '@ncg/data';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconSpriteModule } from 'ng-svg-icon-sprite';
import { combineLatest, firstValueFrom, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';

import { FeatureDetectionService } from '../../core/feature-detection.service';
import { ScrollService } from '../../core/scroll.service';
import { SettingsService } from '../../core/settings.service';
import { TrackingService } from '../../core/tracking.service';
import { LOCATION_QUERY_STORE_CODE } from '../../form/location-select.component';
import { LocationService } from '../../location/location.service';
import { LogosModule } from '../../logos/logos.module';
import { TextSpriteIconModule } from '../../text-sprite-icon/text-sprite-icon.module';
import { UtilsModule } from '../../utils/utils.module';
import { SpotsConfig } from '../spots-config';
import { MapListComponent } from './map-list/map-list.component';
import { MapLocationInfoContactComponent } from './map-location-contact/map-location-info-contact.component';
import { MapLocationInfoComponent } from './map-location-info/map-location-info.component';
import { MapWindowContentComponent } from './map-window-content/map-window-content.component';
import { MapService } from './map.service';
import { SortLocationsByPipe } from './sort-locations-by.pipe';
import { randomId } from '../../core/helpers';

@Component({
    selector: 'ncg-map-container',
    template: ` <div class="map-spot" ncgLoadIn>
        <div class="container" *ngIf="searchForm">
            <form class="map-form" [formGroup]="searchForm" (ngSubmit)="onSearchSubmit()" novalidate #mapForm>
                <div>
                    <div class="map-search level-item py-5 mb-0">
                        <div class="field is-grouped is-not-column">
                            <div class="control">
                                <input
                                    #inputField
                                    class="input map-search__input"
                                    formControlName="searchTerm"
                                    name="searchTerm"
                                    type="text"
                                    [placeholder]="'map.search_placeholder' | translate"
                                    [attr.list]="inputField.value || isTouchDevice ? 'datalist-locations_' + mapId : ''" />
                                <!--
                                            On desktop the datalist can be very long, so we only
                                            show it if the input has value, so the options are fewer.
                                            On iOS it behaves weirdly if dynamic, so we always show
                                            the datalist on touch devices
                                        -->
                                <datalist [id]="'datalist-locations' + mapId">
                                    <!-- We loop twice, to first list cities then zipcodes -->
                                    <option *ngFor="let location of locations | sortLocationsBy: 'city'" [value]="location.address?.city"></option>
                                    <option
                                        *ngFor="let location of locations | sortLocationsBy: 'zipcode'"
                                        [value]="location.address?.zipcode"></option>
                                </datalist>
                            </div>
                            <div class="control">
                                <button
                                    type="submit"
                                    [class.is-loading]="states.processing"
                                    [disabled]="states.processing"
                                    class="button is-filled is-search">
                                    {{ 'map.search' | translate }}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="map-form__sort pb-6">
                        <div class="field is-grouped is-grouped-centered is-grouped-multiline is-not-column" *ngIf="data.showFilter">
                            <div class="control">
                                <label class="radio is-small">
                                    <input type="radio" formControlName="locationCategory" [value]="locationCategoryRetail" />
                                    <span class="checkmark"></span>
                                    <span class="icon">
                                        <span class="icon-text"> {{ 'map.dealer_label' | translate }}</span>
                                        <img [src]="pinMarkerUrl" width="36px" alt="" *ngIf="markerIconUrl" />
                                    </span>
                                </label>
                            </div>
                            <div class="control">
                                <label class="radio is-small">
                                    <input type="radio" formControlName="locationCategory" [value]="locationCategoryWorkshop" />
                                    <span class="checkmark"></span>
                                    <span class="icon">
                                        <span class="icon-text">{{ 'map.service_label' | translate }}</span>
                                        <img [src]="pinServiceMarkerUrl" width="36px" alt="" *ngIf="markerIconUrl" />
                                    </span>
                                </label>
                            </div>
                            <div class="control" *ngIf="data.enableSparePartsFilterOption">
                                <label class="radio is-small">
                                    <input type="radio" formControlName="locationCategory" [value]="locationCategorySpareParts" />
                                    <span class="checkmark"></span>
                                    <span class="icon">
                                        <span class="icon-text">{{ 'map.spareparts_label' | translate }}</span>
                                        <img [src]="pinSparePartsMarkerUrl" width="36px" alt="" *ngIf="markerIconUrl" />
                                    </span>
                                </label>
                            </div>
                            <div class="control" *ngIf="data.enableTestDriveFilterOption">
                                <label class="radio is-small">
                                    <input type="radio" formControlName="locationCategory" [value]="locationCategoryTestDriveOnly" />
                                    <span class="checkmark"></span>
                                    <span class="icon">
                                        <span class="icon-text">{{ 'map.testdriveonly_label' | translate }}</span>
                                        <img [src]="pinTestDriveOnlyMarkerUrl" width="36px" alt="" *ngIf="markerIconUrl" />
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
            <div class="map-toggle is-hidden-desktop">
                <div class="columns is-centered is-gapless is-mobile">
                    <div class="column is-narrow">
                        <div class="buttons">
                            <button type="button" class="button is-clean" [ngClass]="{ 'is-active': isListView }" (click)="toggleView(true)">
                                <span class="icon map-list-icon">
                                    <svg-icon-sprite
                                        [src]="'list'"
                                        [viewBox]="'0 0 30 30'"
                                        [width]="'20px'"
                                        [height]="'20px'"
                                        aria-hidden="true"
                                        classes=""></svg-icon-sprite>
                                </span>
                                <span>{{ 'map.toggle_list' | translate }}</span>
                            </button>
                            <button type="button" class="button is-clean" [ngClass]="{ 'is-active': !isListView }" (click)="toggleView(false)">
                                <span class="icon map-icon">
                                    <svg-icon-sprite
                                        [src]="'map-icon'"
                                        [viewBox]="'0 0 30 30'"
                                        [width]="'30px'"
                                        [height]="'30px'"
                                        aria-hidden="true"
                                        classes=""></svg-icon-sprite>
                                </span>
                                <span>{{ 'map.toggle_map' | translate }}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="columns columns is-centered" *ngIf="!states.locationsFound && !states.isPristine">
                <div class="column is-narrow i">
                    <div class="notification is-primary" *ngIf="locations.length && states.errorMessage">
                        {{ states.errorMessage }}
                    </div>
                    <div class="notification is-primary" *ngIf="!locations.length">
                        {{ 'map.no_locations_found' | translate }}
                    </div>
                </div>
            </div>
            <div class="notification is-primary has-text-centered" *ngIf="isMapScriptLoaded && !listLocations?.length">
                <div>{{ 'map.no_locations_created' | translate }}</div>
            </div>
            <div class="columns map-row is-gapless">
                <div
                    class="map-col map-col--1 column is-5-desktop is-4-widescreen"
                    [ngClass]="{ 'is-shown': isListView, 'is-visible': listLocations?.length }">
                    <ncg-map-list
                        *ngIf="isMapScriptLoaded && listLocations?.length"
                        [isBrandLayout]="isBrandLayout"
                        [locations]="listLocations"
                        [currentItem]="currentLocation"
                        (mapItemCliked)="onMapListItemClicked($event)"
                        [userCurrentPosition]="userCurrentPosition"
                        [currentCategory]="currentCategory"
                        [serviceLink]="data.serviceLink"
                        [testDriveLink]="data.testDriveLink"
                        [hideServiceButton]="data.hideServiceButton"
                        [hideTestDriveButton]="data.hideTestDriveButton"
                        [contactLink]="data.contactLink"
                        [hideContactButton]="data.hideContactButton"
                        [markerIconUrl]="markerIconUrl"
                        [openingHoursLink]="
                            data.showOpeningHoursLink && data.openingHoursLink?.url ? data.openingHoursLink : undefined
                        "></ncg-map-list>
                </div>
                <div class="map-col map-col--2 column is-7-desktop is-8-widescreen" [ngClass]="{ 'is-shown': !isListView, 'is-visible': showMap }">
                    <div class="map-world" *ngIf="isMapScriptLoaded && locations?.length" data-chromatic="ignore">
                        <google-map class="map" height="100%" width="100%" [center]="centerLatLng" [zoom]="zoom" (mapClick)="closeInfo()">
                            <map-marker
                                #marker="mapMarker"
                                *ngFor="let markerPosition of markerPositions"
                                [position]="markerPosition.position"
                                [options]="markerOptions"
                                (mapClick)="markerClick(marker, markerPosition.location)"></map-marker>
                            <map-info-window>
                                <ncg-map-window-content
                                    *ngIf="currentLocation"
                                    [currentCategory]="currentCategory"
                                    [location]="currentLocation"
                                    [serviceLink]="data.serviceLink"
                                    [testDriveLink]="data.testDriveLink"
                                    [hideServiceButton]="data.hideServiceButton"
                                    [hideTestDriveButton]="data.hideTestDriveButton"
                                    [contactLink]="data.contactLink"
                                    [hideContactButton]="data.hideContactButton"
                                    [isBrandLayout]="isBrandLayout"
                                    [openingHoursLink]="
                                        data.showOpeningHoursLink && data.openingHoursLink?.url ? data.openingHoursLink : undefined
                                    "></ncg-map-window-content>
                            </map-info-window>
                        </google-map>
                    </div>
                </div>
            </div>
        </div>
    </div>`,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class MapContainerComponent implements OnInit, OnDestroy {
    private readonly unsubscribe = new Subject<void>();
    private readonly markerZoomLevel = 12;
    private readonly searchRadius = 28; // In km

    private currentParams: Params = {};
    private buildIdQuery = getBuildIdQuery();
    private bounds: google.maps.LatLngBounds;
    private currentSearchTerm = '';
    private currentSearchLatLng?: google.maps.LatLng;
    private sortedLocations: NgcApiLocation[] = [];
    private geocoder: google.maps.Geocoder;
    private settings: ISettingsResponse;

    public readonly locationCategoryRetail: NcgLocationMapCategory = 'retail';
    public readonly locationCategoryWorkshop: NcgLocationMapCategory = 'workshop';
    public readonly locationCategorySpareParts: NcgLocationMapCategory = 'spareparts';
    public readonly locationCategoryTestDriveOnly: NcgLocationMapCategory = 'testdriveonly';

    public locations: NgcApiLocation[] = [];
    public currentLocation?: NgcApiLocation;
    public isListView = true;
    public isMapScriptLoaded = false;
    public userCurrentPosition: ICoordinations = {
        lat: DEFAULT_LAT,
        lng: DEFAULT_LNG,
    }; // Set default coordinates to make sure the map gets rendered
    public listLocations: NgcApiLocation[] = [];
    public centerLatLng = { lat: 54.15790446441528, lng: 13.432382071464572 };
    public markerOptions: google.maps.MarkerOptions;
    public markerPositions: { position: google.maps.LatLngLiteral; location: NgcApiLocation }[] = [];
    public zoom = 8;
    public searchForm?: UntypedFormGroup;
    public currentCategory: NcgLocationMapCategory;
    public showMap = false;
    public states = {
        submitted: false,
        processing: false,
        locationsFound: false,
        isPristine: true,
        errorMessage: '',
    };
    public markerIconUrl = '';
    public pinMarkerUrl = `assets/icons/marker-pin.svg${this.buildIdQuery}`;
    public pinServiceMarkerUrl = `assets/icons/marker-pin-service.svg${this.buildIdQuery}`;
    public pinSparePartsMarkerUrl = `assets/icons/marker-pin-spareparts.svg${this.buildIdQuery}`;
    public pinTestDriveOnlyMarkerUrl = `assets/icons/marker-pin-testdriveonly.svg${this.buildIdQuery}`;
    public isBrandLayout = false;
    public isTouchDevice = true;
    public mapId: string;

    @ViewChild(GoogleMap)
    public map: GoogleMap;

    @ViewChild(MapInfoWindow)
    public infoWindow: MapInfoWindow;

    @ViewChild('mapForm')
    public mapFormElement?: ElementRef;

    @ViewChildren('marker')
    public markers: QueryList<MapMarker>;

    @Input()
    public data: MapSpot;

    constructor(
        private readonly mapService: MapService,
        private readonly locationService: LocationService,
        private readonly cd: ChangeDetectorRef,
        private readonly featureDetectionService: FeatureDetectionService,
        private readonly fb: UntypedFormBuilder,
        private readonly translate: TranslateService,
        private readonly settingsService: SettingsService,
        @Inject(SpotsConfig) private readonly spotsConfig: SpotsConfig,
        private readonly router: Router,
        private readonly location: Location,
        private readonly trackingService: TrackingService,
        private readonly scrollService: ScrollService
    ) {}

    public ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public async ngOnInit() {
        this.isTouchDevice = this.featureDetectionService.isTouchDevice();
        this.currentCategory = this.data.defaultFilterOption;
        this.mapId = randomId('map');

        if (this.featureDetectionService.isBrowser()) {
            this.currentParams = this.getQueryParams();
            this.createForm();
            const [isMapScriptLoaded, settings] = await Promise.all([
                firstValueFrom(this.mapService.initGoogleMapsScripts()),
                firstValueFrom(this.settingsService.get()),
            ]);
            this.settings = settings;
            this.isMapScriptLoaded = isMapScriptLoaded;
            this.setMarkerUrls();
            const locationCategoryFromParams: NcgLocationMapCategory =
                this.currentParams['locationCategory'] || this.currentParams['locationcategory'];

            if (locationCategoryFromParams) {
                const validCategories: NcgLocationMapCategory[] = [
                    this.locationCategoryRetail,
                    this.locationCategoryWorkshop,
                    this.locationCategorySpareParts,
                    this.locationCategoryTestDriveOnly,
                ];
                if (validCategories.includes(locationCategoryFromParams)) {
                    this.currentCategory = locationCategoryFromParams;
                    this.searchForm?.get('locationCategory')?.patchValue(this.currentCategory);
                }
            }

            if (this.isMapScriptLoaded) {
                this.onCategoryChange();
                await this.getLocations(this.data.locationsByStoreCode, this.currentCategory);
                this.showMap = true;
                this.cd.detectChanges(); // We need this to make google-map component to update it's list of markers
                if (this.locations) {
                    this.isBrandLayout = this.spotsConfig.isBrandLayout;
                    this.setDefaultOptions();
                    this.setMarkers();

                    this.cd.detectChanges();
                    const storeCodeFromParams: string =
                        this.currentParams[LOCATION_QUERY_STORE_CODE] || this.currentParams[LOCATION_QUERY_STORE_CODE.toLowerCase()];

                    if (storeCodeFromParams) {
                        const index = this.markerPositions.findIndex((item) => item.location.store_code === storeCodeFromParams);
                        if (index > -1) {
                            const marker = this.markers.toArray()[index];
                            const { location } = this.markerPositions[index];

                            // Map controls likely to overlap infowindow if not called after tilesloaded event.
                            // Note: Not likely to happen locally. Hard to debug.
                            this.map.tilesloaded.pipe(take(1), takeUntil(this.unsubscribe)).subscribe(() => {
                                this.openInfoWindow(marker, location);
                            });
                            this.isListView = false;
                        }
                    } else if (this.map && this.bounds) {
                        if (this.settings?.seoCountry && this.data?.zoomMapToRegion) {
                            try {
                                const countryName = new Intl.DisplayNames(['en'], { type: 'region' }).of(this.settings.seoCountry);
                                await this.geocoder.geocode({ address: countryName }, (results, status) => {
                                    if (status === google.maps.GeocoderStatus.OK && results) {
                                        this.bounds = results[0].geometry.viewport;
                                    } else {
                                        throw Error(status);
                                    }
                                });
                            } catch (e) {
                                console.warn('Region was not valid', e);
                            }
                        }
                        this.map.fitBounds(this.bounds); // We need this to bound markers on the map, before the user has accepted or declined, search by GPS Coords
                        this.cd.markForCheck();

                        await this.searchByUserGps();
                    }
                }
            }
        }
    }

    public async onSearchSubmit() {
        if (this.states.processing) {
            return;
        }
        this.states.isPristine = false;
        this.states.processing = true;
        this.closeInfo();
        this.cd.markForCheck();
        if (this.searchForm) {
            this.currentSearchTerm = this.searchForm.value.searchTerm;

            try {
                const locationLatLng: google.maps.LatLng | undefined = await this.getSearchLocation();
                if (locationLatLng) {
                    this.getClosetsMarker(locationLatLng);
                } else {
                    this.fitToBounds(this.locations);
                    this.listLocations = this.locations;
                }

                this.scrollToMap();
                this.cd.markForCheck();
            } catch (error: any) {
                this.states.errorMessage = error;
                this.states.locationsFound = false;
            }

            this.states.processing = false;
            this.cd.markForCheck();
            this.trackingService.trackMapSearch(this.currentSearchTerm, this.states.locationsFound ? 'results' : 'no results');
        }
    }

    public markerClick(marker: MapMarker, location: NgcApiLocation) {
        if (this.currentLocation?.store_code === location.store_code) {
            this.closeInfo();
            return;
        }
        this.openInfoWindow(marker, location);
        this.panAndZoomToMarker(marker);
    }

    public onMapListItemClicked(item: { currentItem: NgcApiLocation; index: number }) {
        const index = this.markerPositions.findIndex((position) => position.location.store_code === item.currentItem.store_code);
        const marker = this.markers.toArray()[index];

        this.panAndZoomToMarker(marker);
        this.openInfoWindow(marker, item.currentItem);
    }

    public closeInfo() {
        this.updateParams(LOCATION_QUERY_STORE_CODE, '', true);

        if (this.infoWindow) {
            this.infoWindow.close();
            this.currentLocation = undefined;
            this.cd.markForCheck();
        }
    }

    public toggleView(isList: boolean) {
        this.isListView = isList;
    }

    private getQueryParams(): Params {
        return this.router.parseUrl(this.location.path()).queryParams;
    }

    private updateParams(key: string, value: string, clearParam = false) {
        const params = { ...this.currentParams };

        if (clearParam) {
            delete params[key];
        } else {
            // Because we allow case insensitive params, to make it more "fool-proof",
            // we delete those when updating, to avoid double trouble
            const lowerCaseParam = key.toLocaleLowerCase();
            if (params[lowerCaseParam]) {
                delete params[lowerCaseParam];
            }
            params[key] = value;
        }

        this.currentParams = params;
        this.location.replaceState(
            this.router.serializeUrl(
                this.router.createUrlTree([], {
                    queryParams: params,
                })
            ),
            undefined,
            this.location.getState()
        );
    }

    private createForm() {
        this.searchForm = this.fb.group({
            searchTerm: ['', [Validators.required, Validators.minLength(1)]],
            locationCategory: [this.currentCategory],
        });
    }

    private setMarkerUrls() {
        if (this.settings) {
            this.pinMarkerUrl = this.settings.mapMarkerIcon?.url ?? `assets/icons/marker-pin.svg${this.buildIdQuery}`;
            this.pinServiceMarkerUrl = this.settings.mapMarkerServiceIcon?.url ?? `assets/icons/marker-pin-service.svg${this.buildIdQuery}`;
            this.pinSparePartsMarkerUrl = this.settings.mapMarkerSparepartsIcon?.url ?? `assets/icons/marker-pin-spareparts.svg${this.buildIdQuery}`;
            this.pinTestDriveOnlyMarkerUrl =
                this.settings.mapMarkerTestdriveonlyIcon?.url ?? `assets/icons/marker-pin-testdriveonly.svg${this.buildIdQuery}`;
        }
    }

    private async getLocations(locationsByStoreCode: boolean, category: NcgLocationMapCategory | '' = '') {
        const getData = (cat: NcgLocationCategory | '') =>
            (locationsByStoreCode
                ? this.locationService.getLocationsBySiteContext(cat)
                : this.locationService.getLocationsByBrandName('all', cat)
            ).pipe(map((locationsData) => locationsData.aggregations?.locations));

        // If 'retail' then also get 'showroom' and merge locations. NIC-2187.
        const data$ =
            category === 'retail'
                ? combineLatest([getData('retail'), getData('showroom')]).pipe(
                    map((locationsArr) =>
                        [...(locationsArr?.[0] ?? []), ...(locationsArr?.[1] ?? [])].sort((a, b) =>
                            (a.name ?? '') < (b.name ?? '') ? -1 : (a.name ?? '') > (b.name ?? '') ? 1 : 0
                        )
                    )
                )
                : getData(category);

        this.locations = (await firstValueFrom(data$)) ?? [];
        this.listLocations = this.locations;
        this.sortedLocations = this.locations.map((location) => ({ distance: null, ...location })) ?? []; // add distance: null if undefined
        this.cd.markForCheck();
    }

    private async searchByUserGps() {
        try {
            const { coords } = await this.getGeoCoords();
            this.userCurrentPosition = {
                lat: coords.latitude,
                lng: coords.longitude,
            };

            this.currentSearchLatLng = new google.maps.LatLng(this.userCurrentPosition.lat, this.userCurrentPosition.lng);
            await this.getAndSetZipCodeByLatLng();
            this.getClosetsMarker(this.currentSearchLatLng);
        } catch (error: any) {
            console.warn(`Couldn't find user coords. ${error && error.message ? `Message: ${error.message}` : ''}`);
        }

        this.states.processing = false;
        this.cd.markForCheck();
    }

    private getAndSetZipCodeByLatLng(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.geocoder.geocode(
                {
                    location: this.currentSearchLatLng,
                },
                (results, status) => {
                    if (results && status === 'OK') {
                        const result = results[0];
                        if (result) {
                            const addresses = result.address_components;
                            this.currentSearchTerm = this.searchByPostalCode(addresses) ?? result.formatted_address;
                            this.searchForm?.get('searchTerm')?.patchValue(this.currentSearchTerm);
                            return resolve();
                        } else {
                            // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                            return reject();
                        }
                    } else {
                        return reject();
                    }
                }
            );
        });
    }

    private searchByPostalCode(addresses: google.maps.GeocoderAddressComponent[]): string | undefined {
        for (const address of addresses) {
            if (address.long_name && address.types?.includes('postal_code')) {
                return address.long_name;
            }
        }
        return undefined;
    }

    private addMarkers(location: NgcApiLocation) {
        const position = new google.maps.LatLng(Number(location.address?.lat), Number(location.address?.lng));
        this.markerPositions.push({
            position: {
                lat: position.lat(),
                lng: position.lng(),
            },
            location,
        });

        this.bounds.extend(position);
    }

    private setDefaultOptions() {
        this.markerIconUrl = this.pinMarkerUrl;
        this.bounds = new google.maps.LatLngBounds();
        this.geocoder = new google.maps.Geocoder();
        this.setMarkerOptions();
    }

    private setMarkerOptions() {
        if (this.currentCategory === this.locationCategoryWorkshop) {
            this.markerIconUrl = this.pinServiceMarkerUrl;
        } else if (this.currentCategory === this.locationCategoryRetail) {
            this.markerIconUrl = this.pinMarkerUrl;
        } else if (this.currentCategory === this.locationCategoryTestDriveOnly) {
            this.markerIconUrl = this.pinTestDriveOnlyMarkerUrl;
        } else if (this.currentCategory === this.locationCategorySpareParts) {
            this.markerIconUrl = this.pinSparePartsMarkerUrl;
        }

        this.markerOptions = {
            draggable: false,
            icon: this.markerIconUrl,
            animation: google.maps.Animation.DROP,
        };
    }

    private setMarkers() {
        this.locations.forEach((location) => {
            this.addMarkers(location);
        });
    }

    private scrollToMap() {
        this.scrollService.scrollToElement(this.mapFormElement?.nativeElement);
    }

    private onCategoryChange() {
        const category = this.searchForm?.get('locationCategory');
        if (category) {
            category.valueChanges
                .pipe(
                    switchMap((value) => {
                        this.markerPositions = [];
                        this.states.processing = true;
                        this.closeInfo();
                        this.currentCategory = value;
                        this.updateParams(LOCATION_QUERY_STORE_CODE, '', true);
                        this.updateParams('locationCategory', this.currentCategory);
                        this.cd.markForCheck();
                        return this.getLocations(this.data.locationsByStoreCode, this.currentCategory);
                    }),
                    takeUntil(this.unsubscribe)
                )
                .subscribe(() => {
                    if (this.currentSearchLatLng) {
                        this.getClosetsMarker(this.currentSearchLatLng);
                    }
                    this.setMarkers();
                    this.setMarkerOptions();
                    this.states.processing = false;
                    this.cd.markForCheck();
                });
        }
    }

    /**
     * Try to get LatLng for searched city
     */
    private getSearchLocation(): Promise<google.maps.LatLng | undefined> {
        return new Promise((resolve, reject) => {
            this.currentLocation = undefined;
            const country = this.settings.seoCountry;
            const region = `${this.settings.seoLanguage}-${country}`; // Prefix with langcode for better google search

            if (this.currentSearchTerm) {
                this.geocoder.geocode(
                    {
                        address: this.currentSearchTerm,
                        region,
                        componentRestrictions: {
                            country,
                            locality: this.currentSearchTerm,
                        },
                    },
                    (results, status) => {
                        if (results && status === 'OK') {
                            const result = results[0];
                            const searchLocation = result.geometry.location;
                            const locationLatLng: google.maps.LatLng = new google.maps.LatLng(searchLocation.lat(), searchLocation.lng());
                            this.currentSearchLatLng = locationLatLng;
                            return resolve(this.currentSearchLatLng);
                        } else {
                            // Because of issues with geocoder, we do a backup search for zipmatches.
                            // https://issuetracker.google.com/issues/75985322
                            const zipMatch = this.locations.find(
                                (location) => location.address?.zipcode?.replace(/\s+/g, '') === this.currentSearchTerm.replace(/\s+/g, '')
                            );

                            if (zipMatch) {
                                const locationLatLng: google.maps.LatLng = new google.maps.LatLng(
                                    Number(zipMatch.address?.lat),
                                    Number(zipMatch.address?.lng)
                                );
                                this.currentSearchLatLng = locationLatLng;
                                return resolve(this.currentSearchLatLng);
                            } else {
                                console.warn('Geocode was not successful for the following reason: ' + status);
                                this.states.processing = false;
                                return reject(new Error(`${this.translate.instant('map.zero_search_result')}: ${this.currentSearchTerm}`));
                            }
                        }
                    }
                );
            } else {
                return resolve(undefined);
            }
        });
    }

    private getClosetsMarker(latLng: google.maps.LatLng): void {
        this.sortedLocations = this.sortedLocations
            .map((location) => {
                const markerLatLng = new google.maps.LatLng(Number(location.address?.lat), Number(location.address?.lng));
                const distanceInMeters = google.maps.geometry.spherical.computeDistanceBetween(latLng, markerLatLng);
                const distanceInKm = distanceInMeters / 1000;
                const newMarker = {
                    ...location,
                    distance: String(distanceInKm),
                };
                return newMarker;
            })
            .sort((a, b) => Number(a.distance) - Number(b.distance));

        const locationsInRadius = this.sortedLocations.filter(
            (location) => location.distance !== null && Number(location.distance) < this.searchRadius
        );
        this.listLocations = this.settings.featureToggles.isShowAllDealersOnMap ? this.sortedLocations : this.sortedLocations.slice(0, 5);

        if (locationsInRadius.length && locationsInRadius.length > 1) {
            this.fitToBounds(locationsInRadius);
            this.states.locationsFound = true;
        } else if (locationsInRadius.length) {
            const index = this.markerPositions.findIndex((item) => item.location.store_code === locationsInRadius?.[0].store_code);
            const marker = this.markers.toArray()[index];
            if (marker) {
                this.panAndZoomToMarker(marker);
            } else {
                this.fitToBounds(this.locations);
            }

            this.states.locationsFound = true;
        } else {
            this.fitToBounds(this.locations);
            this.states.locationsFound = false;
            this.states.errorMessage = `${this.translate.instant('map.zero_search_result')}: ${this.currentSearchTerm}`;
        }
    }

    /**
     * Fits map around the given list of locations
     *
     * @param listArr NgcApiLocation[]
     */
    private fitToBounds(listArr: NgcApiLocation[]) {
        this.bounds = new google.maps.LatLngBounds(); // reset bounds
        listArr.forEach((location) => {
            const position = new google.maps.LatLng(Number(location.address?.lat), Number(location.address?.lng));
            if (position) {
                this.bounds.extend(position);
            }
        });

        this.map.fitBounds(this.bounds);
        // To make sure the map reacts to zoom changes later on, we must set the zoom to the current level defined by the fitBounds method
        const currentZoom = this.map.getZoom();
        if (currentZoom) {
            this.zoom = currentZoom;
        }
        this.cd.markForCheck();
    }

    private openInfoWindow(marker: MapMarker, location: NgcApiLocation) {
        this.currentLocation = location;
        this.cd.markForCheck();
        this.infoWindow.options = {
            ...this.infoWindow.options,
            minWidth: 260,
        };
        this.updateParams(LOCATION_QUERY_STORE_CODE, this.currentLocation.store_code ?? '');
        this.infoWindow.open(marker, true);
        this.infoWindow.closeclick.pipe(takeUntil(this.unsubscribe)).subscribe(() => this.closeInfo());
    }

    private panAndZoomToMarker(marker: MapMarker) {
        const position = marker.getPosition();
        if (position) {
            this.map.panTo(position);
            this.zoom = this.markerZoomLevel;
        }
        this.cd.markForCheck();
    }

    private getGeoCoords(): Promise<any> {
        return new Promise((resolve, reject) => {
            window.navigator?.geolocation?.getCurrentPosition(resolve, reject, {
                maximumAge: 56000,
                enableHighAccuracy: false,
            });
        });
    }
}

@NgModule({
    imports: [
        CommonModule,
        TranslateModule.forChild(),
        GoogleMapsModule,
        ReactiveFormsModule,
        UtilsModule,
        RouterModule,
        LogosModule,
        IconSpriteModule,
        TextSpriteIconModule,
        SortLocationsByPipe,
    ],
    declarations: [MapContainerComponent, MapListComponent, MapLocationInfoComponent, MapLocationInfoContactComponent, MapWindowContentComponent],
    schemas: [NO_ERRORS_SCHEMA],
})
export class MapContainerComponentModule {}

export interface ICoordinations {
    lat: number;
    lng: number;
}

export const DEFAULT_LAT = 1;
export const DEFAULT_LNG = 1;
